import { useState } from "react";
import {
    Button,
    ButtonColor,
    Container,
    ContainerSize,
    Gap,
    Heading,
    Stack,
    Text,
    Toast,
    ToastBg,
    ToastPlacement,
} from "@rebootui";
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";

const ToastPage = () => {
    const [showBasic, setShowBasic] = useState(false);
    const [showAutohide, setShowAutohide] = useState(false);
    const [showWithImage, setShowWithImage] = useState(false);
    const [showPrimary, setShowPrimary] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showDanger, setShowDanger] = useState(false);
    const [showWarning, setShowWarning] = useState(false);
    const [showStacked, setShowStacked] = useState(false);
    const [showPlacement, setShowPlacement] = useState(false);

    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Toast
            </Heading>

            <Text className="mb-4">
                Push notifications to visitors with a toast. Toggle visibility with <Text el="strong">show</Text>; pass{" "}
                <Text el="strong">onClose</Text> to sync state after the toast finishes hiding.
            </Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Basic toast
            </Heading>
            <Text className="mb-4">
                A toast with header and body.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Button color={ButtonColor.Primary} onClick={() => setShowBasic(true)}>
                            Show basic toast
                        </Button>

                        <Toast.Container placement={ToastPlacement.TopEnd}>
                            <Toast show={showBasic} onClose={() => setShowBasic(false)}>
                                <Toast.Header>
                                    <strong className="me-auto">Bootstrap</strong>
                                    <small>just now</small>
                                </Toast.Header>
                                <Toast.Body>Hello, world! This is a toast message.</Toast.Body>
                            </Toast>
                        </Toast.Container>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ToastBasicCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Auto-hide toast
            </Heading>
            <Text className="mb-4">
                Set <Text el="strong">autohide</Text> to dismiss after <Text el="strong">delay</Text> (milliseconds; default{" "}
                <Text el="strong">5000</Text>). Example uses <Text el="strong">3000</Text>.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Button color={ButtonColor.Secondary} onClick={() => setShowAutohide(true)}>
                            Show auto-hide toast
                        </Button>

                        <Toast.Container placement={ToastPlacement.TopEnd}>
                            <Toast show={showAutohide} onClose={() => setShowAutohide(false)} autohide delay={3000}>
                                <Toast.Header>
                                    <strong className="me-auto">Auto-hide</strong>
                                    <small>3 seconds</small>
                                </Toast.Header>
                                <Toast.Body>This toast closes automatically after 3 seconds.</Toast.Body>
                            </Toast>
                        </Toast.Container>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ToastAutohideCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Toast with image
            </Heading>
            <Text className="mb-4">
                Add an image in the header.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Button color={ButtonColor.Info} onClick={() => setShowWithImage(true)}>
                            Show toast with image
                        </Button>

                        <Toast.Container placement={ToastPlacement.TopEnd}>
                            <Toast show={showWithImage} onClose={() => setShowWithImage(false)}>
                                <Toast.Header>
                                    <img src="https://via.placeholder.com/20" className="rounded me-2" alt="" />
                                    <strong className="me-auto">Bootstrap</strong>
                                    <small>11 mins ago</small>
                                </Toast.Header>
                                <Toast.Body>See? Just like this with an image.</Toast.Body>
                            </Toast>
                        </Toast.Container>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ToastWithImageCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Colored toasts
            </Heading>
            <Text className="mb-4">
                Apply <Text el="strong">ToastBg</Text> variants with the <Text el="strong">bg</Text> prop.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button color={ButtonColor.Primary} onClick={() => setShowPrimary(true)}>
                                Primary
                            </Button>
                            <Button color={ButtonColor.Success} onClick={() => setShowSuccess(true)}>
                                Success
                            </Button>
                            <Button color={ButtonColor.Danger} onClick={() => setShowDanger(true)}>
                                Danger
                            </Button>
                            <Button color={ButtonColor.Warning} onClick={() => setShowWarning(true)}>
                                Warning
                            </Button>
                        </div>

                        <Toast.Container placement={ToastPlacement.TopEnd}>
                            <Toast show={showPrimary} onClose={() => setShowPrimary(false)} bg={ToastBg.Primary}>
                                <Toast.Header>
                                    <strong className="me-auto">Primary toast</strong>
                                    <small className="text-white">now</small>
                                </Toast.Header>
                                <Toast.Body className="text-white">This is a primary colored toast.</Toast.Body>
                            </Toast>
                            <Toast show={showSuccess} onClose={() => setShowSuccess(false)} bg={ToastBg.Success}>
                                <Toast.Header>
                                    <strong className="me-auto">Success toast</strong>
                                    <small className="text-white">now</small>
                                </Toast.Header>
                                <Toast.Body className="text-white">This is a success colored toast.</Toast.Body>
                            </Toast>
                            <Toast show={showDanger} onClose={() => setShowDanger(false)} bg={ToastBg.Danger}>
                                <Toast.Header>
                                    <strong className="me-auto">Danger toast</strong>
                                    <small className="text-white">now</small>
                                </Toast.Header>
                                <Toast.Body className="text-white">This is a danger colored toast.</Toast.Body>
                            </Toast>
                            <Toast show={showWarning} onClose={() => setShowWarning(false)} bg={ToastBg.Warning}>
                                <Toast.Header>
                                    <strong className="me-auto">Warning toast</strong>
                                    <small>now</small>
                                </Toast.Header>
                                <Toast.Body>This is a warning colored toast.</Toast.Body>
                            </Toast>
                        </Toast.Container>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ToastColoredCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Stacked toasts
            </Heading>
            <Text className="mb-4">
                Multiple toasts in one container stack vertically — add spacing with utility classes such as{" "}
                <Text el="strong">mt-2</Text>.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Button color={ButtonColor.Primary} onClick={() => setShowStacked(true)}>
                            Show stacked toasts
                        </Button>

                        <Toast.Container placement={ToastPlacement.TopEnd}>
                            {showStacked && (
                                <>
                                    <Toast show={showStacked} onClose={() => setShowStacked(false)}>
                                        <Toast.Header>
                                            <strong className="me-auto">First toast</strong>
                                            <small>just now</small>
                                        </Toast.Header>
                                        <Toast.Body>This is the first toast message.</Toast.Body>
                                    </Toast>
                                    <Toast show={showStacked} onClose={() => setShowStacked(false)} className="mt-2">
                                        <Toast.Header>
                                            <strong className="me-auto">Second toast</strong>
                                            <small>2 seconds ago</small>
                                        </Toast.Header>
                                        <Toast.Body>This is the second toast message.</Toast.Body>
                                    </Toast>
                                    <Toast show={showStacked} onClose={() => setShowStacked(false)} className="mt-2">
                                        <Toast.Header>
                                            <strong className="me-auto">Third toast</strong>
                                            <small>5 seconds ago</small>
                                        </Toast.Header>
                                        <Toast.Body>This is the third toast message.</Toast.Body>
                                    </Toast>
                                </>
                            )}
                        </Toast.Container>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ToastStackedCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Placement
            </Heading>
            <Text className="mb-4">
                Choose a corner with <Text el="strong">ToastPlacement</Text> on <Text el="strong">Toast.Container</Text> (
                <Text el="strong">BottomEnd</Text> here).
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Button color={ButtonColor.Success} onClick={() => setShowPlacement(true)}>
                            Show bottom-end toast
                        </Button>

                        <Toast.Container placement={ToastPlacement.BottomEnd}>
                            <Toast show={showPlacement} onClose={() => setShowPlacement(false)}>
                                <Toast.Header>
                                    <strong className="me-auto">Bottom-end position</strong>
                                    <small>now</small>
                                </Toast.Header>
                                <Toast.Body>This toast uses ToastPlacement.BottomEnd.</Toast.Body>
                            </Toast>
                        </Toast.Container>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ToastPlacementCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    );
};

export default ToastPage;

const ToastBasicCode = `const [showBasic, setShowBasic] = useState(false);

<Button onClick={() => setShowBasic(true)}>Show basic toast</Button>

<Toast.Container placement={ToastPlacement.TopEnd}>
    <Toast show={showBasic} onClose={() => setShowBasic(false)}>
        <Toast.Header>
            <strong className="me-auto">Bootstrap</strong>
            <small>just now</small>
        </Toast.Header>
        <Toast.Body>Hello, world! This is a toast message.</Toast.Body>
    </Toast>
</Toast.Container>`;

const ToastAutohideCode = `const [showAutohide, setShowAutohide] = useState(false);

<Button onClick={() => setShowAutohide(true)}>Show auto-hide toast</Button>

<Toast.Container placement={ToastPlacement.TopEnd}>
    <Toast show={showAutohide} onClose={() => setShowAutohide(false)} autohide delay={3000}>
        <Toast.Header>
            <strong className="me-auto">Auto-hide</strong>
            <small>3 seconds</small>
        </Toast.Header>
        <Toast.Body>This toast closes automatically after 3 seconds.</Toast.Body>
    </Toast>
</Toast.Container>`;

const ToastWithImageCode = `const [showWithImage, setShowWithImage] = useState(false);

<Button onClick={() => setShowWithImage(true)}>Show toast with image</Button>

<Toast.Container placement={ToastPlacement.TopEnd}>
    <Toast show={showWithImage} onClose={() => setShowWithImage(false)}>
        <Toast.Header>
            <img src="https://via.placeholder.com/20" className="rounded me-2" alt="" />
            <strong className="me-auto">Bootstrap</strong>
            <small>11 mins ago</small>
        </Toast.Header>
        <Toast.Body>See? Just like this with an image.</Toast.Body>
    </Toast>
</Toast.Container>`;

const ToastColoredCode = `<Toast.Container placement={ToastPlacement.TopEnd}>
    <Toast show={showPrimary} onClose={() => setShowPrimary(false)} bg={ToastBg.Primary}>
        <Toast.Header>
            <strong className="me-auto">Primary toast</strong>
            <small className="text-white">now</small>
        </Toast.Header>
        <Toast.Body className="text-white">This is a primary colored toast.</Toast.Body>
    </Toast>
    <Toast show={showSuccess} onClose={() => setShowSuccess(false)} bg={ToastBg.Success}>
        <Toast.Header>
            <strong className="me-auto">Success toast</strong>
            <small className="text-white">now</small>
        </Toast.Header>
        <Toast.Body className="text-white">This is a success colored toast.</Toast.Body>
    </Toast>
    <Toast show={showDanger} onClose={() => setShowDanger(false)} bg={ToastBg.Danger}>
        <Toast.Header>
            <strong className="me-auto">Danger toast</strong>
            <small className="text-white">now</small>
        </Toast.Header>
        <Toast.Body className="text-white">This is a danger colored toast.</Toast.Body>
    </Toast>
    <Toast show={showWarning} onClose={() => setShowWarning(false)} bg={ToastBg.Warning}>
        <Toast.Header>
            <strong className="me-auto">Warning toast</strong>
            <small>now</small>
        </Toast.Header>
        <Toast.Body>This is a warning colored toast.</Toast.Body>
    </Toast>
</Toast.Container>`;

const ToastStackedCode = `{showStacked && (
    <Toast.Container placement={ToastPlacement.TopEnd}>
        <Toast show={showStacked} onClose={() => setShowStacked(false)}>
            <Toast.Header>
                <strong className="me-auto">First toast</strong>
                <small>just now</small>
            </Toast.Header>
            <Toast.Body>This is the first toast message.</Toast.Body>
        </Toast>
        <Toast show={showStacked} onClose={() => setShowStacked(false)} className="mt-2">
            <Toast.Header>
                <strong className="me-auto">Second toast</strong>
                <small>2 seconds ago</small>
            </Toast.Header>
            <Toast.Body>This is the second toast message.</Toast.Body>
        </Toast>
        <Toast show={showStacked} onClose={() => setShowStacked(false)} className="mt-2">
            <Toast.Header>
                <strong className="me-auto">Third toast</strong>
                <small>5 seconds ago</small>
            </Toast.Header>
            <Toast.Body>This is the third toast message.</Toast.Body>
        </Toast>
    </Toast.Container>
)}`;

const ToastPlacementCode = `<Toast.Container placement={ToastPlacement.BottomEnd}>
    <Toast show={showPlacement} onClose={() => setShowPlacement(false)}>
        <Toast.Header>
            <strong className="me-auto">Bottom-end position</strong>
            <small>now</small>
        </Toast.Header>
        <Toast.Body>This toast uses ToastPlacement.BottomEnd.</Toast.Body>
    </Toast>
</Toast.Container>`;
